import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSurveyPermissions } from '../../../../models/hooks/useSurveyPermissions';
import { useEventLogging } from '../../../../models/Logging';
import { copyToClipboard } from '../../../../models/UiUtils';
import { useAccountStripeApiPublicApiKeys } from '../../../../queries';
import type { IDonationFrequencySettings, IDonationOptions } from '../../../../surveys/models/Api';
import {
	ConfirmationDialog,
	DefaultDiscardChangesConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Page } from '../../../components-v2/PageLayout';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { FabContext } from '../../../components/FabContext';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { QRCodeModal } from '../../../components/QRCodeModal';
import { EyeIcon } from '../../../components/svgs/icons/EyeIcon';
import { ListOfQuestionsIcon } from '../../../components/svgs/icons/ListOfQuestionsIcon';
import { TabSettingsIcon } from '../../../components/svgs/icons/TabSettingsIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../components/Tabs';
import { useClonableSurvey } from '../../../hooks/useClonableSurvey';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { AdditionalQuestions } from '../shared/components/AdditionalQuestions';
import { ReorderQuestionsButton } from '../shared/components/ReorderQuestionsButton';
import { ReorderQuestionsModalComponent } from '../shared/components/ReorderQuestionsModal/ReorderQuestionsModal';
import { SurveyActions } from '../shared/components/SurveyActions';
import { SurveyHeader } from '../shared/components/SurveyHeader';
import { SurveySettings } from '../shared/components/SurveySettings';
import { UnsavedChangesWarning } from '../shared/components/UnsavedChangesWarning';
import { useUnsavedChanges } from '../shared/hooks/useUnsavedChanges';
import { hasCustomFormChanges } from '../shared/utils/customFormComparison';
import { PageSetup } from './components';
import { DonationOptions } from './components/DonationOptions';
import { DonorInformation } from './components/DonorInformation';
import { PreviewDonationSurveyForm } from './components/PreviewDonationSurveyForm';
import { useDonationSurveyFormInitialization, useDonationSurveyMutations } from './hooks';
import { styleSheet } from './styles';
import {
	DonationDonorInfoCollection,
	DonationServiceFeeCalculationMethod,
	DonationSurveyFormData,
	TabValues,
	validationSchema,
} from './utils';

const { useCallback } = React;

const DEFAULT_NAME = 'Untitled Donation Survey';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IEditSurveyComponent {
	PageSetup: React.FC<{ survey: Api.IDonationSurvey }>;
	DonorInformation: React.FC<{ survey: Api.IDonationSurvey }>;
	DonationOptions: React.FC<{ survey: Api.IDonationSurvey; customerPortalUrl?: string }>;
	AdditionalQuestions: React.FC<{ isEdit: boolean; isClone: boolean }>;
}

const DonationSurveyBase: React.FC<IProps> & Partial<IEditSurveyComponent> = observer(props => {
	const { className, styles = [], impersonationContext } = props;
	const _userSession = useUserSession();
	const surveyPermissions = useSurveyPermissions();
	const params = useParams<{ id: string }>();
	const history = useHistory();
	const { logApiError } = useEventLogging('DonationSurvey');
	const errorMessages = useErrorMessages();

	const {
		surveyData: survey,
		isClone,
		isEdit,
		isLoading: surveyDataIsLoading,
	} = useClonableSurvey<Api.IDonationSurvey>(params.id, impersonationContext, 'donation');

	// Check Stripe configuration for donation surveys
	const stripePublicKeys = useAccountStripeApiPublicApiKeys({ impersonationContext });
	const isStripeConfigured = React.useMemo(() => {
		return _userSession.account?.features?.stripePayment?.enabled && Boolean(stripePublicKeys?.data);
	}, [_userSession.account?.features?.stripePayment?.enabled, stripePublicKeys?.data]);

	// Get customer portal URL for recurring donations
	const customerPortalUrl = _userSession.account?.features?.stripePayment?.customerPortalUrl;

	// Redirect if trying to create a new donation survey without Stripe configured
	React.useEffect(() => {
		if (!isEdit && !surveyDataIsLoading && !isStripeConfigured) {
			toast.error(
				'Cannot create donation survey. Please enable and configure Stripe in Admin Settings to accept donations.'
			);
			history.replace('/surveys/donation');
		}
	}, [isEdit, surveyDataIsLoading, isStripeConfigured, history]);

	const [showEditSurveyName, setShowEditSurveyName] = React.useState(false);
	const [showReorderModal, setShowReorderModal] = React.useState(false);
	const [selectedTab, setSelectedTab] = React.useState<TabValues>(TabValues.Settings);
	const [isQRModalOpen, setIsQRModalOpen] = React.useState(false);
	const [showDiscardChangesConfirmation, setShowDiscardChangesConfirmation] = React.useState(false);
	const [pendingNavigation, setPendingNavigation] = React.useState<string | null>(null);
	const defaultValues = React.useMemo<DonationSurveyFormData>(() => {
		const defaultOneTimeSettings = {
			enabled: true,
			hasDonationValues: true,
			donationValues: [{ value: 25 }, { value: 50 }, { value: 100 }, { value: 250 }],
			allowCustomDonationAmount: true,
			hasMinimumDonationAmount: false,
			minimumDonationAmount: 0,
		};

		return {
			donationPageTitle: 'My Page Title',
			causeDetails: '',
			allowProgramSpecificDonations: false,
			programSpecificDonation: [],
			hasExpirationDate: false,
			image: null,
			imageSource: null,
			updateExistingContacts: false,
			collectDonorPhone: DonationDonorInfoCollection.NO_COLLECT,
			collectDonorAddress: DonationDonorInfoCollection.NO_COLLECT,

			// Legacy fields (kept for backward compatibility)
			hasDonationValues: true,
			donationValues: [{ value: 25 }, { value: 50 }, { value: 100 }, { value: 250 }],
			allowCustomDonationAmount: true,
			hasMinimumDonationAmount: false,
			minimumDonationAmount: 0,

			// New frequency-specific settings
			oneTimeSettings: defaultOneTimeSettings,
			monthlySettings: {
				enabled: false,
				hasDonationValues: true,
				donationValues: [{ value: 12 }, { value: 25 }, { value: 50 }, { value: 125 }], // OneTime ÷ 2 (floored)
				allowCustomDonationAmount: true,
				hasMinimumDonationAmount: false,
				minimumDonationAmount: 0,
			},
			annualSettings: {
				enabled: false,
				hasDonationValues: true,
				donationValues: [{ value: 300 }, { value: 600 }, { value: 1200 }, { value: 3000 }], // OneTime × 12
				allowCustomDonationAmount: true,
				hasMinimumDonationAmount: false,
				minimumDonationAmount: 0,
			},

			allowCustomerToCoverServiceFees: false,
			serviceFeeCalculationMethod: DonationServiceFeeCalculationMethod.STRIPE,
			serviceFeeFixedAmount: 0,
			serviceFeePercentage: 0,
			customForm: {
				name: '',
				fields: [],
			},
			tagGenerationSettings: undefined,
		};
	}, []);

	const methods = useForm<DonationSurveyFormData>({
		defaultValues: {
			...defaultValues,
		},
		resolver: yupResolver(validationSchema),
		mode: 'onSubmit',
		reValidateMode: 'onChange',
	});
	const { handleSubmit, setValue, watch, formState, reset, getValues } = methods;

	// Initialize form with survey data when editing
	useDonationSurveyFormInitialization({
		survey,
		setValue,
	});

	const formValues = watch();

	// Use modern mutation hook pattern (follows EventRegistrationSurvey and CustomSurvey)
	const { createSurveyMutation, updateSurveyMutation, reorderSurveyFieldsMutation } = useDonationSurveyMutations({
		impersonationContext,
		history,
		errorMessages,
		logApiError,
		getValues,
		reset,
		setValue,
		watch,
	});

	const isArchived = Boolean(survey?.archivedDate);

	const onCopyLinkClicked = () => {
		if (copyToClipboard(survey?.anonymousLink)) {
			toast.success('Link copied to clipboard');
			return;
		}
		toast.error('Error copying link to clipboard');
	};

	const onQRCodeClick = () => {
		setIsQRModalOpen(true);
	};

	const handleQRModalClose = () => {
		setIsQRModalOpen(false);
	};

	const handleReorderQuestions = () => {
		setShowReorderModal(true);
	};

	const handleReorderModalClose = useCallback(
		(result?: { reorderedQuestions?: any }, cancel?: boolean) => {
			setShowReorderModal(false);
			if (!cancel && result?.reorderedQuestions) {
				reorderSurveyFieldsMutation.mutate({
					surveyId: survey.id,
					fieldIds: result.reorderedQuestions.map((field: Api.IFormField<string>) => field.id),
				});
			}
		},
		[reorderSurveyFieldsMutation, survey]
	);

	const isLoading = React.useMemo(
		() => (surveyDataIsLoading && isEdit) || createSurveyMutation.isPending || updateSurveyMutation.isPending,
		[surveyDataIsLoading, isEdit, createSurveyMutation.isPending, updateSurveyMutation.isPending]
	);

	const customFormErrors = Object.keys(formState.errors).some(x => x.startsWith('customForm'));

	const questions = formValues?.customForm?.fields || [];
	const hasMultipleQuestions = questions.length > 1;
	// Track if customForm fields have meaningful changes by comparing specific properties
	const customFormFieldsHaveChanges = React.useMemo(
		() => hasCustomFormChanges(survey?.customForm?.fields, formValues.customForm?.fields),
		[survey?.customForm?.fields, formValues.customForm?.fields]
	);

	// Track unsaved changes
	const hasUnsavedChanges = useUnsavedChanges({
		surveyType: 'donation',
		isEdit,
		isClone,
		formState,
		additionalConditions: [customFormFieldsHaveChanges],
	});

	/**
	 * Intercepts navigation clicks when there are unsaved changes
	 * Shows confirmation dialog to prevent accidental data loss
	 */
	const handleNavigationClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				setPendingNavigation(targetPath);
				setShowDiscardChangesConfirmation(true);
			}
		},
		[hasUnsavedChanges]
	);

	/**
	 * Handles user's response to discard changes confirmation dialog
	 */
	const handleDiscardChangesConfirmation = useCallback(
		(option?: IConfirmationDialogOption<boolean>, isCancel?: boolean) => {
			setShowDiscardChangesConfirmation(false);

			// User clicked "Discard" - proceed with navigation
			if (!isCancel && option?.representedObject === true && pendingNavigation) {
				history.push(pendingNavigation);
			}

			// Clear pending navigation regardless of choice
			setPendingNavigation(null);
		},
		[pendingNavigation, history]
	);

	const breadcrumbPageTitle = useCallback(() => {
		if (isClone) {
			return 'Clone Donation Survey';
		}
		return params.id ? (survey?.name ?? 'Edit Donation Survey') : 'New Donation Survey';
	}, [params.id, survey?.name, isClone]);

	const buildServiceFeeSettings = (formData: DonationSurveyFormData) => {
		return {
			enabled: formData.allowCustomerToCoverServiceFees ?? false,
			allowCustomerToCoverServiceFees: formData.allowCustomerToCoverServiceFees ?? false,
			calculationMethod: formData.serviceFeeCalculationMethod ?? DonationServiceFeeCalculationMethod.STRIPE,
			...(formData.serviceFeeCalculationMethod === DonationServiceFeeCalculationMethod.FIXED && {
				fixedFeeAmount: formData.serviceFeeFixedAmount ?? 0,
			}),
			...(formData.serviceFeeCalculationMethod === DonationServiceFeeCalculationMethod.PERCENTAGE && {
				percentageFee: formData.serviceFeePercentage ?? 0,
			}),
		};
	};

	const buildMinimumAmount = (formData: DonationSurveyFormData) => {
		if (!formData.hasMinimumDonationAmount) {
			return;
		}

		return {
			minimumAmount: formData.minimumDonationAmount ?? 0,
		};
	};

	const buildDonationOptionField = (value: DonationDonorInfoCollection, updateExisting: boolean) => {
		return {
			collect: value === DonationDonorInfoCollection.COLLECT,
			require: value === DonationDonorInfoCollection.REQUIRE,
			updateExisting,
		};
	};

	const buildDefaultRequiredDonorInfoCollection = (updateExisting: boolean) => {
		return {
			collect: false,
			require: true,
			updateExisting,
		};
	};

	const buildFrequencySettings = (settings: DonationSurveyFormData['oneTimeSettings']): IDonationFrequencySettings => {
		return {
			enabled: settings.enabled,
			enableSuggestedAmounts: settings.hasDonationValues,
			// API layer expects decimal values in dollars
			suggestedAmounts: settings.donationValues.map(v => v.value),
			allowCustomAmount: settings.allowCustomDonationAmount,
			requireMinimumAmount: settings.hasMinimumDonationAmount,
			// API layer expects decimal values in dollars
			minimumAmount: settings.minimumDonationAmount,
		};
	};

	/**
	 * Builds the donation options payload for API submission.
	 *
	 * This function ensures data integrity by:
	 * 1. Converting form enum values to API boolean flags
	 * 2. Enforcing at least one enabled donation frequency
	 * 3. Mapping frequency-specific settings to API structure
	 * 4. Maintaining legacy field compatibility for older backend versions
	 *
	 * Safety Check: If all frequencies are disabled (which shouldn't be possible with
	 * proper validation), force-enables one-time donations to prevent creating a
	 * donation survey with no way to donate.
	 *
	 * @param formData - The donation survey form data from React Hook Form
	 * @returns The donation options object ready for API submission
	 */
	const buildDonationOptions = (formData: DonationSurveyFormData): IDonationOptions => {
		// NOTE: At least one frequency is always enabled due to "last one standing" rule
		// enforced by useEffect in DonationOptions component. No safety check needed here.
		const updateExisting = formData.updateExistingContacts ?? false;

		return {
			collectDonorName: buildDefaultRequiredDonorInfoCollection(updateExisting),
			collectDonorEmail: buildDefaultRequiredDonorInfoCollection(updateExisting),
			collectDonorPhone: buildDonationOptionField(formData.collectDonorPhone, updateExisting),
			collectDonorAddress: buildDonationOptionField(formData.collectDonorAddress, updateExisting),
			sendConfirmationEmail: true,
			donationDescription: formData.causeDetails,
			title: formData.donationPageTitle,
			image: formData.image,

			// Legacy flat structure (for backward compatibility with old backend versions)
			enableSuggestedAmounts: formData.oneTimeSettings.hasDonationValues,
			// API layer expects decimal values in dollars
			suggestedAmounts: formData.oneTimeSettings.donationValues.map(value => value.value),
			allowCustomAmount: formData.oneTimeSettings.allowCustomDonationAmount,
			requireMinimumAmount: formData.oneTimeSettings.hasMinimumDonationAmount,
			// API layer expects decimal values in dollars
			minimumAmount: formData.oneTimeSettings.minimumDonationAmount,

			// New frequency-specific settings (always send all to avoid null reference errors on backend)
			// Compute allowRecurringDonations from individual frequency settings
			allowRecurringDonations: formData.monthlySettings.enabled || formData.annualSettings.enabled,
			oneTimeDonationSettings: buildFrequencySettings(formData.oneTimeSettings),
			monthlyDonationSettings: buildFrequencySettings(formData.monthlySettings),
			annualDonationSettings: buildFrequencySettings(formData.annualSettings),
		};
	};

	const onSubmit = (formData: DonationSurveyFormData) => {
		const payload: Api.IDonationSurvey = {
			_type: 'DonationSurvey',
			name: formData.name,
			serviceFeeSettings: buildServiceFeeSettings(formData),
			donationOptions: buildDonationOptions(formData),
			// Only include campaign IDs if the toggle is ON
			availableCampaignIds: formData.allowProgramSpecificDonations
				? formData.programSpecificDonation?.map(campaign => campaign.id)
				: [],
			customForm: formData.customForm,
			tagGenerationSettings: formData.tagGenerationSettings,
			...buildMinimumAmount(formData),
		};

		if (isEdit) {
			updateSurveyMutation.mutate({
				survey: { ...survey, ...payload },
			});
		} else {
			createSurveyMutation.mutate({
				survey: payload,
			});
		}
	};

	const isPageSetupWithErrors = React.useMemo(() => {
		return Object.keys(formState.errors).some(x =>
			[
				'donationPageTitle',
				'causeDetails',
				'allowProgramSpecificDonations',
				'programSpecificDonation',
				'image',
				'hasExpirationDate',
				'expirationDate',
			].includes(x)
		);
	}, [formState.errors]);

	const isDonorInformationWithErrors = React.useMemo(() => {
		return Object.keys(formState.errors).some(x => ['collectDonorPhone', 'collectDonorAddress'].includes(x));
	}, [formState.errors]);

	const isDonationOptionsWithErrors = React.useMemo(() => {
		return Object.keys(formState.errors).some(x =>
			[
				'allowRecurringDonations',
				'supportedRecurringFrequencies',
				'hasDonationValues',
				'donationValues',
				'allowCustomDonationAmount',
				'hasMinimumDonationAmount',
				'minimumDonationAmount',
				'allowCustomerToCoverServiceFees',
				'serviceFeeCalculationMethod',
				'serviceFeeFixedAmount',
				'serviceFeePercentage',
				'oneTimeSettings',
				'monthlySettings',
				'annualSettings',
			].includes(x)
		);
	}, [formState.errors]);

	const settingsTabsWithErrors = React.useMemo(() => {
		const withErrors = [];

		if (isPageSetupWithErrors) {
			withErrors.push('page-setup');
		}

		if (isDonorInformationWithErrors) {
			withErrors.push('donor-information');
		}

		if (isDonationOptionsWithErrors) {
			withErrors.push('donation-options');
		}

		return withErrors;
	}, [isPageSetupWithErrors, isDonorInformationWithErrors, isDonationOptionsWithErrors]);

	const settingsErrors = React.useMemo(() => {
		return settingsTabsWithErrors.length > 0;
	}, [settingsTabsWithErrors]);

	return (
		<FormProvider {...methods}>
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/surveys/donation' onClick={e => handleNavigationClick(e, '/surveys/donation')}>
										Donation Surveys
									</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>{breadcrumbPageTitle()}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
			/>
			<form
				className={`${css(styleSheet.donationSurveyContainer, ...styles)} edit-survey ${className || ''}`}
				onSubmit={handleSubmit(onSubmit)}
			>
				<Page styles={[styleSheet.page]}>
					<MultiContainerHeader
						fullscreenHeader={`${isEdit ? (isArchived ? 'View' : 'Edit') : 'New'} Donation Survey`}
					/>
					<FabContext appearance={{ hidden: true }} />
					<header className={css(styleSheet.header)}>
						<SurveyHeader
							surveyName={formValues.name || DEFAULT_NAME}
							showEditName={showEditSurveyName}
							onEditToggle={() => setShowEditSurveyName(!showEditSurveyName)}
							onNameChange={name => setValue('name', name)}
							validationError={formState.errors?.name?.message}
							isEdit={isEdit}
						/>
						<SurveyActions
							onCopyLink={onCopyLinkClicked}
							isLoading={isLoading}
							isSaveDisabled={showEditSurveyName}
							showCopyLink={!!survey?.id}
							surveyLink={survey?.anonymousLink}
							onQRCodeClick={onQRCodeClick}
							showViewReport={!!survey?.id && surveyPermissions.canViewReportingDonationSurvey()}
							surveyId={survey?.id}
							surveyType='DonationSurvey'
						/>
					</header>
					<UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
					<TabRoot
						className={`tab-view ${css(styleSheet.tabViewContainer)}`}
						value={selectedTab}
						onValueChange={tValue => setSelectedTab(tValue as TabValues)}
					>
						<TabList styles={[styleSheet.tabList]}>
							<IconTabTrigger
								value={TabValues.Settings}
								icon={<TabSettingsIcon fillColor='currentColor' />}
								styles={settingsErrors ? [styleSheet.tabTextError] : []}
							>
								Settings
							</IconTabTrigger>
							<IconTabTrigger
								value={TabValues.Questions}
								styles={customFormErrors ? [styleSheet.tabTextError] : []}
								icon={<ListOfQuestionsIcon fillColor='currentColor' />}
							>
								Questions
							</IconTabTrigger>
							{survey?.anonymousLink ? (
								<IconTabTrigger value={TabValues.Preview} icon={<EyeIcon fillColor='currentColor' />}>
									Preview
								</IconTabTrigger>
							) : null}
						</TabList>
						<TabContent value={TabValues.Settings} className={css(styleSheet.tabContent, styleSheet.newTabContent)}>
							<SurveySettings
								styles={[styleSheet.surveySettingsContainer]}
								tabRootStyles={[styleSheet.surveySettingsTabRoot]}
								title='Donation Survey Settings'
								subtitle={
									<>
										<p>
											Once users have donated, you will be able to send emails to those who answered
											&quot;attending&quot; vs &quot;not attending&quot;.
										</p>
									</>
								}
								tabs={[
									{
										title: 'Page Setup',
										key: 'page-setup',
										content: <DonationSurvey.PageSetup survey={survey} />,
									},
									{
										title: 'Donor Information',
										key: 'donor-information',
										content: <DonationSurvey.DonorInformation survey={survey} />,
									},
									{
										title: 'Donation Options',
										key: 'donation-options',
										content: <DonationSurvey.DonationOptions survey={survey} customerPortalUrl={customerPortalUrl} />,
									},
								]}
							/>
						</TabContent>
						<TabContent value={TabValues.Questions} className={css(styleSheet.tabContent, styleSheet.tabContentLight)}>
							<div className={css(styleSheet.bodyBackground)} />

							<div className={css(styleSheet.formContainer)}>
								<ReorderQuestionsButton
									onReorderQuestions={handleReorderQuestions}
									hasMultipleQuestions={hasMultipleQuestions}
									isLoading={isLoading}
								/>
								<div className={css(styleSheet.additionalQuestionsContainer)}>
									<DonationSurvey.AdditionalQuestions isEdit={isEdit} isClone={isClone} />
								</div>
							</div>
						</TabContent>
						{survey?.anonymousLink && (
							<TabContent value={TabValues.Preview} className={css(styleSheet.tabContent, styleSheet.newTabContent)}>
								<PreviewDonationSurveyForm survey={survey} />
							</TabContent>
						)}
					</TabRoot>
				</Page>
			</form>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
			<ReorderQuestionsModalComponent
				modalProps={{
					isOpen: showReorderModal,
					onRequestClose: handleReorderModalClose,
				}}
				questions={questions}
				surveyId={params.id || ''}
				impersonationContext={impersonationContext}
			/>
			<QRCodeModal
				modalProps={{
					isOpen: isQRModalOpen,
					onRequestClose: handleQRModalClose,
				}}
				url={survey?.anonymousLink || ''}
				title='QR Code'
				filename={`donation-survey-qr-${survey?.id || 'code'}`}
			/>
			{showDiscardChangesConfirmation && (
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: showDiscardChangesConfirmation,
						onRequestClose: handleDiscardChangesConfirmation,
					}}
					options={DefaultDiscardChangesConfirmationOptions}
					title='Are you sure you want to discard changes?'
				/>
			)}
		</FormProvider>
	);
});

export const DonationSurvey = inject(ImpersonationContextKey)(DonationSurveyBase);

DonationSurvey.PageSetup = PageSetup;
DonationSurvey.DonorInformation = DonorInformation;
DonationSurvey.DonationOptions = DonationOptions;
DonationSurvey.AdditionalQuestions = AdditionalQuestions;
